import { FC, useCallback, useEffect, useState, useMemo } from "react"
import AnalysisTemplate from "components/layout/templates/analysisTemplate"
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Button, Panel, Tab, Tabs } from "@appkit4/react-components";
import { ListType, UserType } from "routes/analysis/types/analysis";
import { getDate, parseMatches, parseLockStatus } from "services/common";
import { LoaderWrapper, toastMessage } from "components/common/helpers";
import ToolTip from "components/common/tooltip";
import { useAtomValue } from "jotai";
import { filterAtom } from "jotai/store";
import { FetchAnalysisData } from "routes/analysis/queries/analysis";
import { exportExcel } from "services/export";
import { FetchAllUserTests, FetchUserData } from "./queries/user";
import UserDataBox from "./components/UserDataBox";
import UserRoles from "./components/UserRoles";
import UserTests from "./components/UserTests";
import UserTransactions from "./components/UserTransactions";
import UserProfiles from "./components/UserProfiles";
import SameRolesProfiles from "./components/SameRolesProfiles";
import { MatchTable, UserMatchData, MatchWrapper, TestData } from "../types/user";
import { fullMatchHeaders } from "../types/exportHeaders";

enum UserTab {
  TESTS,
  TRANSACTIONS,
  ROLES,
  PROFILES,
  SAME
}

const User: FC<{ index: number }> = ({ index }) => {
  const [tabIndex, setTabIndex] = useState(index);
  const [getTests, setGetTests] = useState(false);
  const navigate = useNavigate();
  const { analysisId, userId, testId, transactionId, roleId } = useParams();

  const userFilter = useAtomValue(filterAtom);

  const { data: userData, isPending: isUserPending, error: errorUser } = FetchUserData(analysisId, userId, userFilter);
  const { data: analysisData, isPending: isAnalysisPending, error: errorAnalysis } = FetchAnalysisData(analysisId);

  const { data: allTests, error } = FetchAllUserTests(analysisId, userId, getTests);

  const businessProcesses = useMemo(() => {
    return Array.from(new Set(userData?.tests?.map((m: TestData) => m.businessProcess.split(":")[1])));
  }, [userData]);

  const showPage = (tabIndex: number) => {
    switch (tabIndex) {
      case UserTab.TESTS:
        return <UserTests businessProcesses={businessProcesses} userData={userData} />
      case UserTab.TRANSACTIONS:
        return <UserTransactions userData={userData} />
      case UserTab.ROLES:
        return <UserRoles userData={userData} />
      case UserTab.PROFILES:
        return <UserProfiles userData={userData} />
      case UserTab.SAME:
        return <SameRolesProfiles userData={userData} />
    }
  }

  const exportProcess = useCallback((testData: MatchWrapper) => {
    if (!analysisId || !userId || !userData) return;
    let temp = analysisData;
    if (!temp) return;
    let pageHeaders = [
      ["Insights - Access Risks"],
      ["Client:", temp.client?.name || ""],
      ["SAP System:", `${temp.sapSystem?.sapSystemName} - ${temp.sapSystem?.sapClient} - ${temp.sapSystem?.sapNickname}`],
      ["Extraction date", getDate(temp.extractionDate || "")],
      ["Username:", userData.userName],
      ["Type", UserType[userData.type].name],
      ["Name", "All Access Risk matches"]
    ]
    setGetTests(false);
    let result: MatchTable[] = [];
    let userMatchData: UserMatchData = {
      analysisId: analysisId,
      data: testData.data,
      id: "",
      type: ListType.Test,
      name: testData.name,
      description: testData.description,
      userId: userId
    };
    for (let test of userData.tests) {
      result = [...result, ...parseMatches(userMatchData, userData, ListType.All, test.testId, false)]
    }
    exportExcel(result.filter(f => f.function), fullMatchHeaders, `${userData.userName}-All-Matches`, pageHeaders);
  }, [analysisData, analysisId, userData, userId]);

  const exportAll = async () => {
    toastMessage("Exporting data");
    setGetTests(true);
  }

  const getUserName = () => <>
    <ToolTip content={UserType[userData?.type || "A"].name} position="bottom">
      <span
        className={`Appkit4-icon ${UserType[userData?.type || "A"].icon} mr-2`}
      ></span>
    </ToolTip>
    {userData?.userName}
  </>

  useEffect(() => {
    if (testId)
      setTabIndex(UserTab.TESTS);
    if (transactionId)
      setTabIndex(UserTab.TRANSACTIONS);
    if (roleId)
      setTabIndex(UserTab.ROLES);
  }, [setTabIndex, analysisId, testId, transactionId, userData, userId, roleId]);

  useEffect(() => {
    if (allTests) {
      exportProcess(allTests);
    }
    if (error) {
      toastMessage("Unable to export data, please try again.");
    }
  }, [allTests, getTests, error, exportProcess]);
  return (
    <AnalysisTemplate>
      <LoaderWrapper loading={[isAnalysisPending, isUserPending]} errors={[errorAnalysis, errorUser]}>
        <Breadcrumb>
          <BreadcrumbItem>Analysis</BreadcrumbItem>
          <BreadcrumbItem>Users</BreadcrumbItem>
          <BreadcrumbItem>{userData?.userName}</BreadcrumbItem>
        </Breadcrumb>
        <UserDataBox userData={userData} />
        <Panel title={getUserName()} style={{ marginTop: 24 }}>
          <div className="flex w-full gap-4 mb-6">
            <div className="basis-1/6">
              <p className="ap-font-medium">Full name</p>
              <p>{userData?.fullName}</p>
            </div>
            <div className="basis-1/6">
              <p className="ap-font-medium">Lock status</p>
              <p>{userData && parseLockStatus(parseInt(userData.lockStatus) || 0)}</p>
            </div>
            <div className="basis-1/6">
              <p className="ap-font-medium">User group</p>
              <p>{userData?.userGroup || "None assigned"}</p>
            </div>
            <div className="basis-1/6">
              <p className="ap-font-medium">Last logon</p>
              <p>{userData && getDate(userData.lastLogon)}</p>
            </div>
            <div className="basis-1/6">
              <p className="ap-font-medium">Validity</p>
              <p>{userData && userData.validity.split(" - ").map(m => getDate(m.split(" ")[0])).join(" - ")}</p>
            </div>
            <div className="basis-1/6">
              <p className="ap-font-medium">Reference user</p>
              <p>{userData?.refUserName || "None"}</p>
            </div>
          </div>
          <div>
            <Button onClick={exportAll} loading={getTests} kind="secondary"><span className="Appkit4-icon icon-table-data-outline" ></span>Export all matches</Button>
          </div>
        </Panel>
        <Tabs type="underline" activeIndex={tabIndex} onTabChange={(i: number) => {
          switch (i) {
            case UserTab.TESTS:
              navigate(`/analysis/${analysisId}/user/${userId}/test`);
              setTabIndex(0);
              break;
            case UserTab.TRANSACTIONS:
              navigate(`/analysis/${analysisId}/user/${userId}/transaction`);
              setTabIndex(1);
              break;
            case UserTab.ROLES:
              navigate(`/analysis/${analysisId}/user/${userId}/role`);
              setTabIndex(2);
              break;
            case UserTab.PROFILES:
              navigate(`/analysis/${analysisId}/user/${userId}/profile`);
              setTabIndex(3);
              break;
            case UserTab.SAME:
              navigate(`/analysis/${analysisId}/user/${userId}/samerolesprofiles`);
              setTabIndex(4);
              break;
          }
        }}>
          <Tab label="Tests with matches" value="Tests" />
          <Tab label="Transactions with matches" value="Transactions" />
          <Tab label="Assigned roles" value="Roles" />
          <Tab label="Assigned profiles" value="Profiles" />
          <Tab label="Users with same roles/profiles" value="Same" />
        </Tabs>
        {
          showPage(tabIndex)
        }
      </LoaderWrapper>
    </AnalysisTemplate>
  )
}

export default User;